import { isNiriIndicationOn } from './../../../../assets/consts';
import { RxService } from 'app/rx/services/rx.service';
import { Injectable } from '@angular/core';
import md5 from 'md5';
import { of } from 'rxjs';
import { catchError, tap, finalize } from 'rxjs/operators';
import { TimberService } from '@logging/timber.service';
import { ModalIcon, ModalKeys, NotificationService } from '@shared/notification/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { KnownComponentsNames } from '@core/components-names';
import { StickyHeaderService } from 'app/services/stickyHeaderService/stickyHeader.service';
import { SnapshotResults } from '@itero/itero-client-communication';


@Injectable({ providedIn: 'root' })
export class CaptureSupportService {
	private readonly captureDataKey = 'FULL_SCREEN';
	private readonly animationTimeWithoutThumbnail = 300;
	private readonly animationTimeWithThumbnail = 8000;

	constructor(
		private rxService: RxService,
		private timberService: TimberService,
		private translateService: TranslateService,
		private notificationService: NotificationService,
		private stickyHeaderService: StickyHeaderService,
	) { }

	saveScreenShot(screenshots: object, orderId: string) {
		return this.rxService.uploadCaptureIOSimFile(orderId, this.removeMD5KeysAndSetBase64(screenshots)).pipe(
			catchError((err) => {
				this.timberService.error(err, {
					module: 'IosimPlusWrapperComponent',
					extendedParameters: {
						screenshotsData: JSON.stringify(screenshots),
					},
				});
				return of(null);
			})
		);
	}

	saveScreenShotNotIosim(screenshots: object, orderId: string) {
		return this.rxService
			.uploadScreenshotFile(orderId, this.removeMD5KeysAndSetBase64(screenshots))
			.pipe(
				tap(() => this.stickyHeaderService.downloadScreenshotsEnabled$.next(true))
			);
	}

	saveSnapshots(snapshots: SnapshotResults['data']['snapshots'], orderId: string) {
		return this.rxService
			.uploadSnapshots(orderId, snapshots)
			.pipe(
				tap(() => {
					this.stickyHeaderService.downloadScreenshotsEnabled$.next(true);
					this.stickyHeaderService.snapshotResults$.next(null);
				})
			);
	}

	getCaptureAnimationTime(isDrawingToolAllowed: boolean): number {
		return isDrawingToolAllowed ? this.animationTimeWithThumbnail : this.animationTimeWithoutThumbnail;
	}

	isScreenShotsDataValid(screenshots: object) {
		let validData = true;
		for (const key in screenshots) {
			if (key !== isNiriIndicationOn && !key.includes('MD5')) {
				const md5Value = md5(screenshots[key]);
				const md5Key = `${key}_MD5`;

				if (md5Value !== screenshots[md5Key]) {
					validData = false;
					break;
				}
			}
		}
		return validData;
	}

	markInvalidImagesForLogging(screenshots: object) {
		const badImagesToLog = {};
		Object.keys(screenshots)
			.filter((key) => key !== isNiriIndicationOn && key.includes('MD5'))
			.forEach((key) => {
				if (md5(screenshots[key]) !== screenshots[`${key}_MD5`]) {
					badImagesToLog[key] = screenshots[key];
				}
			});

		return badImagesToLog;
	}

	showError(data?: any, page: KnownComponentsNames = KnownComponentsNames.IosimPlus) {
		return of(data).pipe(
			tap((_) => {
				this.notificationService.show(
					this.translateService.instant('Errors.Error_download_file_title'),
					this.translateService.instant('Orders.Capture_Upload_Failed'),
					{ buttonCombination: ModalKeys.Ok, icon: ModalIcon.Error }
				);
			}),
			tap((data) => {
				let extendedParameters = {};
				if (typeof data === 'string') {
					extendedParameters = { data };
				} else {
					const screenshotsData = JSON.stringify(this.markInvalidImagesForLogging(data));
					extendedParameters = { screenshotsData };
				}

				this.timberService.error(`Invalid images from ${page}`, {
					module: page.toString(),
					extendedParameters,
				});
			})
		);
	}

	getFullScreenKey(data) {
		return Object.keys(data).find((key) => key.includes(this.captureDataKey));
	}

	getCapturedImageDataForPatientGallery(capturedImageData: object, origin?: string | undefined) {
		if (!capturedImageData) {
			return undefined;
		}

		const data = capturedImageData as ImageData;

		const isNiriIndicationOn = data.isNiriIndicationOn;

		return Object.entries(data)
			.filter(
				([key, value]) =>
					!key.toLowerCase().includes('md5') &&
					key.toLowerCase() !== 'isniriindicationon' &&
					(key.toLowerCase().includes('full_screen') ||
						key.toLowerCase().includes('3dmodel') ||
						key.toLowerCase().includes('color') ||
						key.toLowerCase().includes('niri') ||
						key.toLowerCase().includes('axi_full_screen') ||
						key.toLowerCase().includes('timelapse_selected_scan') ||
						key.toLowerCase().includes('timelapse_current_scan'))
			)
			.map(([key, value]) => ({
				imageType: this.getImageType(key, isNiriIndicationOn, origin),
				imageData: value.toString(),
			}));
	}
	
	removeScreenShotObjEmptyKeys(obj: any): any {
		return Object.keys(obj)
			.filter((key) => obj[key] !== '')
			.reduce((acc, key) => {
				acc[key] = obj[key];
				return acc;
			}, {});
	}
	
	private getImageType(key: string, isNiriIndicationOn: boolean, origin: string | undefined): ImageTypeEnum {
		const lowerKey = key.toLowerCase();
		if (origin && origin.includes('iosim-screenshots')) {
			return ImageTypeEnum.Iosim;
		} else if (lowerKey.includes('timelapse_selected_scan')) {
			return ImageTypeEnum.TimelapseSelectedScan;
		} else if (lowerKey.includes('timelapse_current_scan')) {
			return ImageTypeEnum.TimelapseCurrentScan;
		} else if (lowerKey.includes('axi_full_screen')) {
			// Explicitly check for axi_full_screen
			return ImageTypeEnum.AxiFullScreen;
		} else if (lowerKey.includes('full_screen')) {
			return isNiriIndicationOn ? ImageTypeEnum.FullScreenWithNiriIndication : ImageTypeEnum.FullScreen;
		} else if (lowerKey.includes('3dmodel')) {
			return ImageTypeEnum.ThreeDModel;
		} else if (lowerKey.includes('color')) {
			return ImageTypeEnum.Color;
		} else if (lowerKey.includes('niri')) {
			return ImageTypeEnum.Niri;
		}
	}

	private removeMD5KeysAndSetBase64(captureData: object) {
		const screenshots = {};
		Object.entries(captureData).forEach(([key, value]) => {
			if (!key.includes('_MD5')) {
				const stringToReplace = /data:image\/(png|jpeg);base64,/gi;
				screenshots[key] = key !== isNiriIndicationOn ? value.replace(stringToReplace, '') : value;
			}
		});

		return screenshots;
	}
}

interface ImageData {
	isNiriIndicationOn: boolean;
	[key: string]: string | boolean;
}

enum ImageTypeEnum {
	FullScreen = 'FullScreen',
	FullScreenWithNiriIndication = 'FullScreenWithNiriIndication',
	Niri = 'Niri',
	ThreeDModel = 'ThreeDModel',
	Color = 'Color',
	Iosim = 'Iosim',
	AxiFullScreen = 'AxiFullScreen',
	TimelapseSelectedScan = 'TimelapseSelectedScan',
	TimelapseCurrentScan = 'TimelapseCurrentScan',
}
